import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import { Banner, type BannerColors } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon, type IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { RichText } from '@sunwebgroup/uds-ui/rich-text'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import * as React from 'react'
import { handleKeyDown } from '@/helpers/keyboardNavigation'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { updatePerBookingService } from '@/scopes/checkout/domain/actions'
import { createAncillariesClickedEvent } from '@/scopes/checkout/domain/analytics'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { bannerColorMap, ServiceType } from '@/scopes/checkout/domain/types'
import { BannerType } from '@/scopes/common/domain/types/banner'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import genericStyles from '../GenericService/GenericService.module.css'
import styles from './CancellationInsuranceService.module.css'

export function CancellationInsuranceService({ item }: { item: ServiceItem }) {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.CancellationInsurance)
  const { sendEvent } = useAnalytics()
  const { componentTitle, componentDescription, popupTitle, popupDescription, popupReadMoreLabel } = item

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updatePerBookingService({ value })(service))

      const optionClicked = service.options.find(option => option.value === value)
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.percentage?.toString() ?? '',
          type: optionClicked?.label || '',
          description: 'select ancillary with comparison table',
          element: 'button',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  useSendAncillaryAvailable(service, 'selectCancellationInsurance')

  if (!service)
    return null

  const { options, banners, selectedValue } = service

  return (
    <CheckoutSection
      title={componentTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_title' })}
      description={componentDescription}
      glyph={getIconForService(ServiceType.CancellationInsurance)}
      moreInformationTitle={popupTitle}
      moreInformationDescription={popupDescription}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      {service.features && service.features.length > 0 && (
        <div className={styles.featuresContainer}>
          <div className={styles.table}>
            <div className={styles.features}>
              <Typography className={styles.topInfo} variant="body-m-bold-snug">
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_top_info' })}
              </Typography>
              <ul className={styles.featuresList}>
                {service.features?.map(({ code, icon, description, extraContent }) => {
                  return (
                    <FeaturesListItemView key={code} code={code} icon={icon} description={description} extraContent={extraContent ?? {}} />
                  )
                })}
              </ul>
            </div>
            <ul className={styles.columns}>
              {options.map(({ label, badge, percentage, percentageText, value, features: optionFeatures, modalInformation }) => {
                const isSelected = value === selectedValue
                return (
                  <li key={value} className={cn(styles.optionColumn, isSelected && styles.optionColumnSelected)}>
                    <div className={styles.optionColumnHeading}>
                      <Typography variant="body-m-bold-snug">{label}</Typography>
                      {percentage && (
                        <div className={styles.percentageSection}>
                          {percentage && <Typography className={styles.percentage} variant="body-m-bold-snug">{percentage}</Typography>}
                          {percentageText && <Typography className={styles.percentage} variant="body-s-regular-flat">{percentageText}</Typography>}
                        </div>
                      )}
                    </div>
                    <ul className={styles.optionFeaturesList}>
                      {optionFeatures?.map((featureLabel, index) => (
                        <li className={styles.optionFeaturesListItem} key={index}>
                          {featureLabel && (
                            <Typography variant="body-s-regular-flat" dangerouslySetInnerHTML={{ __html: featureLabel }} />
                          )}
                          {!featureLabel && <Icon className={styles.crossIcon} glyph="Dismiss" size={24} />}
                        </li>
                      ))}
                    </ul>
                    <div className={styles.optionColumnFooter}>
                      {!isSelected && (
                        <Button variant="secondary" onClick={() => handleValueChange(value)} data-qa="cancelins-service-comparison-table-option">
                          {intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_select_button' })}
                        </Button>
                      )}
                      {isSelected && <Typography className={styles.selectedText} variant="body-m-bold-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_selected_text' })}</Typography>}
                      { modalInformation?.title && modalInformation?.body && <OptionColumnExtraInformation title={modalInformation?.title} body={modalInformation?.body} />}
                    </div>
                    {badge && <Typography className={styles.badge} variant="body-s-bold-flat">{badge}</Typography>}
                  </li>
                )
              })}
            </ul>
          </div>
          {banners.map(({ title, text, type = 'info' }, index) => (
            <Banner
              key={`banner-${index}`}
              color={bannerColorMap[type as BannerType] as BannerColors}
              size="M"
            >
              <Banner.TextsWrapper>
                {title && <Banner.Text>{title}</Banner.Text>}
                {text && <Banner.SupportingText innerHTML={text} />}
              </Banner.TextsWrapper>
            </Banner>
          ))}
        </div>
      )}
      {!service.features && (
        <CancellationInsuranceServicePerBooking />
      )}
    </CheckoutSection>
  )
}

function OptionColumnExtraInformation({ title, body }: { title: string, body: string }) {
  const [showModal, setShowModal] = React.useState(false)
  const intl = useIntl()
  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }

  if (!title || !body)
    return null

  return (
    <>
      <Sheet.Root open={showModal} onClose={closeModal}>
        <Sheet.Trigger>
          <span className={styles.moreInformationLink} tabIndex={0} onKeyDown={e => handleKeyDown(e, openModal)} role="button" onClick={openModal}>
            <Typography variant="body-s-regular-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_meer_info' })}</Typography>
          </span>
        </Sheet.Trigger>
        <Sheet.Body side="right">
          <Sheet.Header titleLabel={title} closeLabel={intl.formatMessage({ id: 'close' })}>
          </Sheet.Header>
          <Sheet.Content>
            <RichText innerHTML={body} />
          </Sheet.Content>
        </Sheet.Body>
      </Sheet.Root>
    </>
  )
}

function FeaturesListItemView({ code, icon, description, extraContent }: { code: string, icon: string | null, description: string, extraContent: Record<string, string> }) {
  const intl = useIntl()
  const [showModal, setShowModal] = React.useState(false)

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }

  return (
    <li className={styles.featuresListItem} key={code}>
      <div className={styles.featureDetails}>
        {icon && <Icon className={styles.featureItemIcon} glyph={icon as IconGlyph} size={24} />}
        <span>
          <Typography className={styles.itemDescription} variant="body-xs-regular-snug">
            {description}
            {' '}
          </Typography>
          {extraContent.linkText && extraContent.body && (
            <Sheet.Root open={showModal} onClose={closeModal}>
              <Sheet.Trigger>
                {extraContent.linkText && (
                  <span className={styles.moreInformationLink} tabIndex={0} onKeyDown={e => handleKeyDown(e, openModal)} role="button" onClick={openModal}>
                    <Typography variant="body-xs-regular-snug">{extraContent.linkText}</Typography>
                  </span>
                )}
              </Sheet.Trigger>
              <Sheet.Body side="right">
                <Sheet.Header titleLabel={extraContent.title} closeLabel={intl.formatMessage({ id: 'close' })}>
                </Sheet.Header>
                <Sheet.Content>
                  <RichText innerHTML={extraContent.body} />
                </Sheet.Content>
              </Sheet.Body>
            </Sheet.Root>
          )}
        </span>
      </div>
    </li>
  )
}

function CancellationInsuranceServicePerBooking() {
  const { service, setServiceValue } = useService(ServiceType.CancellationInsurancePerBooking)

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updatePerBookingService({ value })(service))
    },
    [setServiceValue, service],
  )

  if (!service)
    return null

  const { options, selectedValue } = service

  return (
    <SelectableRadioButtonGroup value={selectedValue ?? undefined} onValueChange={handleValueChange}>
      <div className={genericStyles.optionItemsContainer}>
        {options.map(({ label, badge, percentage, value }) => (
          <div className={genericStyles.itemOptionWrapper} key={value}>
            <SelectableListItem value={value}>
              <div className={genericStyles.itemOption}>
                <div className={genericStyles.itemOptionControl}>
                  <RadioButton value={value} data-qa="cancelins-service-per-booking-option" />
                </div>
                <div className={genericStyles.itemOptionTwoLinesContentWrapper}>
                  <div className={genericStyles.itemOptionContent}>
                    {badge && <Tag className={genericStyles.itemOptionTag} variant="success-tinted" size="M">{badge}</Tag>}
                    <TitleListCell
                      size="L"
                      title={label}
                    />
                  </div>
                  <div className={genericStyles.itemOptionTrailing}>
                    <span className={genericStyles.optionPrice}>
                      {percentage}
                    </span>
                  </div>
                </div>
              </div>
            </SelectableListItem>
          </div>
        ),
        )}
      </div>
    </SelectableRadioButtonGroup>
  )
}
